import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import axiosInstance from '@utils/interceptor'

// Normalize the raw API type into a dashboardType, defaulting null/unknown to Risk.
const toDashboardType = (type?: string | null): FADashboardType =>
  type === FADashboardType.Attribution ? FADashboardType.Attribution : FADashboardType.Risk

// Keep the raw type; add a display-only dashboardType (null/unknown → Risk).
const toDashboard = (raw: DashboardCanvasResponse): Dashboard => ({
  ...raw,
  dashboardType: toDashboardType(raw.type),
})

// UI shape: raw type preserved + a derived dashboardType for display/logic.
export interface Dashboard {
  userId: string;
  userName: string;
  dateCreated: string;
  dateModified: string;
  context: string;
  type?: string | null;
  dashboardType: FADashboardType;
  dashboardName: string;
  dashboardDisplayName?: string;
  dashboardDescription: string;
  isPrivate: boolean;
  isLocked: boolean;
  isDefault: boolean;
  isEditable?: boolean;
  currentUserOwned: boolean;
  jsonValue: Record<string, any>;
}

export interface DashboardWidget {
  id: string;
  type: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  config: Record<string, any>;
}

// GET /dashboard/api/v1/dashboards/metadata returns Dashboard[] directly
export type DashboardListResponse = Dashboard[];

// Raw shape returned by / sent to the dashboard API (type may be null/missing).
export interface DashboardCanvasResponse {
  userId: string;
  userName: string;
  dateCreated: string;
  dateModified: string;
  context: string;
  type?: string | null;
  dashboardName: string;
  dashboardDisplayName?: string;
  dashboardDescription: string;
  isPrivate: boolean;
  isLocked: boolean;
  isDefault: boolean;
  isEditable?: boolean;
  currentUserOwned: boolean;
  jsonValue: Record<string, any>;
}

class DashboardService {
  /**
   * Get metadata for all dashboards visible to the current user (their own + public).
   * GET /dashboard/api/v1/dashboards/metadata
   */
  async getDashboards(context: string): Promise<DashboardListResponse> {
    try {
      const response = await axiosInstance.get('/dashboard/api/v1/dashboards/metadata', { params: { context } });
      return (response.data as DashboardCanvasResponse[]).map(toDashboard);
    } catch (error) {
      console.error('Error fetching dashboards:', error);
      throw error;
    }
  }

  /**
   * Get specific dashboard details for canvas display
   * Uses different endpoints based on dashboard privacy
   */
  async getDashboard(dashboardName: string, context: string, userId: string, currentUserOwned: boolean = true): Promise<Dashboard> {
    try {
      const endpoint = currentUserOwned
        ? `/dashboard/api/v1/dashboard?dashboardName=${dashboardName}&context=${context}`
        : `/dashboard/api/v1/publicDashboard?userId=${userId}&dashboardName=${dashboardName}&context=${context}`;

      const response = await axiosInstance.get(endpoint);
      return toDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      throw error;
    }
  }

  /**
   * Delete dashboard
   * DELETE /api/v1/dashboard
   */
  async deleteDashboard(dashboardName: string, context: string): Promise<void> {
    try {
      await axiosInstance.delete(`/dashboard/api/v1/dashboard?dashboardName=${encodeURIComponent(dashboardName)}&context=${encodeURIComponent(context)}`)
    } catch (error) {
      console.error('Error deleting dashboard:', error)
      throw error
    }
  }

  /**
   * Create/edit dashboard
   * PUT /dashboard/api/v1/dashboard
   */
  async putDashboard(payload: Dashboard): Promise<Dashboard> {
    try {
      // Strip derived dashboardType; send raw `type` untouched so it round-trips.
      const { currentUserOwned, isEditable, dashboardDisplayName, dashboardType, ...body } = payload
      const response = await axiosInstance.put(
        '/dashboard/api/v1/dashboard',
        body
      );

      return toDashboard(response.data);
    } catch (error) {
      console.error('Error creating dashboard:', error);
      throw error;
    }
  }

  /**
   * Check whether the current user is authorized to view the Report Dashboard page.
   * GET /reportDashboard/api/v1/canRunDashboard
   */
  async canRunDashboard(): Promise<boolean> {
    const response = await axiosInstance.get<{ authorized: boolean }>('/reportDashboard/api/v1/canRunDashboard')
    return response.data?.authorized === true
  }

  /**
   * Check whether the current user is authorized to view the Manage Dashboards page.
   * GET /reportDashboard/api/v1/canManageDashboard
   */
  async canManageDashboard(): Promise<boolean> {
    const response = await axiosInstance.get<{ authorized: boolean }>('/reportDashboard/api/v1/canManageDashboard')
    return response.data?.authorized === true
  }

  /**
   * Check whether the current user is authorized to administer dashboards (e.g. see the Report Schedules tab).
   * GET /reportDashboard/api/v1/canAdminManageDashboard
   */
  async canAdminManageDashboard(): Promise<boolean> {
    const response = await axiosInstance.get<{ authorized: boolean }>('/reportDashboard/api/v1/canAdminManageDashboard')
    return response.data?.authorized === true
  }

  /**
   * Admin Mode: get full dashboard details (including jsonValue) for any user's
   * dashboard, so it can be duplicated via "Save As".
   * GET /dashboard/api/v1/admin/dashboard
   */
  async getDashboardAdmin(dashboardName: string, context: string, ownerUserId: string): Promise<Dashboard> {
    try {
      const response = await axiosInstance.get('/dashboard/api/v1/admin/dashboard', {
        params: { ownerUserId, context, dashboardName },
      });
      return toDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard (admin):', error);
      throw error;
    }
  }

  /**
   * Admin Mode: get metadata for every dashboard under a context, across all users.
   * GET /dashboard/api/v1/admin/dashboards/metadata
   */
  async getAllDashboardsMetadata(context: string): Promise<DashboardListResponse> {
    try {
      const response = await axiosInstance.get('/dashboard/api/v1/admin/dashboards/metadata', { params: { context } });
      return (response.data as DashboardCanvasResponse[]).map(toDashboard);
    } catch (error) {
      console.error('Error fetching all dashboards (admin):', error);
      throw error;
    }
  }

  /**
   * Admin Mode: delete any user's dashboard.
   * DELETE /dashboard/api/v1/admin/dashboard
   */
  async adminDeleteDashboard(dashboardName: string, context: string, ownerUserId: string): Promise<void> {
    try {
      await axiosInstance.delete(`/dashboard/api/v1/admin/dashboard?dashboardName=${encodeURIComponent(dashboardName)}&context=${encodeURIComponent(context)}&ownerUserId=${encodeURIComponent(ownerUserId)}`)
    } catch (error) {
      console.error('Error deleting dashboard (admin):', error)
      throw error
    }
  }
}

export const dashboardService = new DashboardService();
